/*
 * Developer / Owner Access Control Console.
 *
 * Route: /admin/rbac
 *
 * Only the Developer role (Missy) can hit the mutation endpoints — the
 * backend enforces this. This UI hides mutation controls for anyone else,
 * but the server is the source of truth.
 *
 * Tabs:
 *  - Staff Directory: everyone with an RBAC assignment
 *  - Roles & Permissions: visual permission editor, clone/reset/create
 *  - Assignments: assign / revoke a role to/from a user
 *  - Audit Log: full RBAC history
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Layers, ClipboardList, Search, Plus, Trash2,
  Copy, RotateCcw, Save, Lock, ChevronDown, ChevronRight, Loader2,
  UserPlus, X, KeyRound, Building2, Filter, Bell, Check, XCircle,
} from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DemoBanner from "@/components/DemoBanner";

const TABS = [
  { key: "directory",   label: "Staff Directory",       icon: Users },
  { key: "roles",       label: "Roles & Permissions",   icon: Layers },
  { key: "assignments", label: "Assignments",           icon: UserPlus },
  { key: "pending",     label: "Pending Approvals",     icon: ClipboardList },
  { key: "audit",       label: "Audit Log",             icon: ClipboardList, developer_only: true },
];

export default function AdminRbac() {
  const { user } = useAuth();
  const [tab, setTab] = useState("directory");
  const [me, setMe] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [roles, setRoles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [audit, setAudit] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---------- data loaders ----------
  const loadCatalog = useCallback(async () => {
    const r = await api.get("/admin/rbac/catalog");
    setCatalog(r.data);
  }, []);

  const loadRoles = useCallback(async () => {
    const r = await api.get("/admin/rbac/roles");
    setRoles(r.data.roles || []);
  }, []);

  const loadStaff = useCallback(async () => {
    const r = await api.get("/admin/rbac/staff");
    setStaff(r.data.staff || []);
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const r = await api.get("/admin/rbac/audit?limit=200");
      setAudit(r.data.entries || []);
    } catch (e) {
      // 403 is expected for non-Developer accounts; show a friendly empty
      // state instead of an axios error dump.
      if (e?.response?.status === 403) {
        setAudit([]);
      } else {
        toast.error(e?.response?.data?.detail || "Failed to load audit log");
      }
    }
  }, []);

  const loadAssignments = useCallback(async () => {
    const r = await api.get("/admin/rbac/assignments");
    setAssignments(r.data.assignments || []);
  }, []);

  const loadPending = useCallback(async () => {
    const r = await api.get("/admin/rbac/pending");
    setPending(r.data.pending || []);
  }, []);

  const loadMe = useCallback(async () => {
    const r = await api.get("/admin/rbac/me");
    setMe(r.data);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await loadMe();
        await Promise.all([loadCatalog(), loadRoles(), loadStaff(), loadAssignments(), loadPending()]);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load RBAC data");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadMe, loadCatalog, loadRoles, loadStaff, loadAssignments, loadPending]);

  useEffect(() => {
    if (tab === "audit") loadAudit();
    if (tab === "pending") loadPending();
  }, [tab, loadAudit, loadPending]);

  const isDev = !!me?.is_developer;

  if (loading) {
    return (
      <div className="min-h-[80vh] grid place-items-center bg-[#0a0a0d]">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  if (!me?.username) {
    return (
      <div className="min-h-[80vh] grid place-items-center bg-[#0a0a0d]">
        <div className="text-zinc-500">You must be logged in.</div>
      </div>
    );
  }

  if ((me.roles?.length ?? 0) === 0 && !user?.is_demo) {
    return (
      <div className="min-h-[80vh] grid place-items-center bg-[#0a0a0d]">
        <div className="text-center">
          <Lock className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
          <div className="text-zinc-400 sc-heading">No staff role assigned</div>
          <div className="text-xs text-zinc-600 mt-2">The Developer must assign you a role before you can access this console.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-[#0a0a0d] pb-20">
      <div className="max-w-[1600px] mx-auto px-6 pt-5">
        <DemoBanner />
      </div>
      {/* Header */}
      <div className="border-b border-white/5 bg-gradient-to-b from-[#0F0F14] to-[#0a0a0d]">
        <div className="max-w-[1600px] mx-auto px-6 py-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-[rgba(0,240,255,0.08)] border border-[rgba(0,240,255,0.25)] grid place-items-center">
            <ShieldCheck className="w-5 h-5 text-[#00F0FF]" />
          </div>
          <div>
            <div className="sc-heading text-lg text-white">Access Control</div>
            <div className="text-xs text-zinc-500 sc-mono">
              Enterprise RBAC · <span className="text-[#00F0FF]">{me.username}</span>
              {isDev && <span className="ml-2 px-1.5 py-0.5 rounded bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-[10px]">DEVELOPER</span>}
              <span className="ml-2 text-zinc-600">· {me.roles.join(", ")}</span>
            </div>
          </div>
        </div>
        <div className="max-w-[1600px] mx-auto px-6 flex gap-1 border-t border-white/5">
          {TABS.filter(t => !t.developer_only || isDev).map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            const badge = t.key === "pending" ? pending.length : 0;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`rbac-tab-${t.key}`}
                className={`px-4 py-3 text-xs sc-heading flex items-center gap-1.5 border-b-2 transition-colors ${
                  active ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
                {badge > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[color:var(--sc-orange)]/20 border border-[color:var(--sc-orange)]/40 text-[10px] text-[color:var(--sc-orange)]">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 pt-5">
        {tab === "directory"   && <DirectoryTab staff={staff} roles={roles} catalog={catalog} isDev={isDev} onReload={loadStaff} />}
        {tab === "roles"       && <RolesTab roles={roles} catalog={catalog} isDev={isDev} onReload={loadRoles} />}
        {tab === "assignments" && <AssignmentsTab assignments={assignments} roles={roles} me={me} onReload={async () => { await loadAssignments(); await loadStaff(); await loadPending(); }} />}
        {tab === "pending"     && <PendingTab pending={pending} me={me} onReload={async () => { await loadPending(); await loadAssignments(); await loadStaff(); }} />}
        {tab === "audit"       && (isDev
          ? <AuditTab audit={audit} onReload={loadAudit} />
          : <div className="term-card p-10 text-center text-sm text-zinc-400" data-testid="rbac-audit-locked">
              <Lock className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
              The RBAC audit log is available to the Developer only.
            </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 * Staff Directory
 * ============================================================ */
function DirectoryTab({ staff, roles, catalog, isDev, onReload }) {
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return staff.filter(s => {
      if (ql && !s.username.toLowerCase().includes(ql)) return false;
      if (dept && !s.departments.includes(dept)) return false;
      if (roleFilter && !s.role_keys.includes(roleFilter)) return false;
      return true;
    });
  }, [staff, q, dept, roleFilter]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by username…"
            className="pl-9 bg-[#0F0F14] border-white/10 h-9 text-sm"
            data-testid="rbac-directory-search"
          />
        </div>
        <select
          value={dept}
          onChange={(e) => setDept(e.target.value)}
          className="bg-[#0F0F14] border border-white/10 rounded px-3 h-9 text-xs text-zinc-200"
          data-testid="rbac-directory-dept-filter"
        >
          <option value="">All departments</option>
          {(catalog?.departments || []).map(d => (
            <option key={d.key} value={d.key}>{d.label}</option>
          ))}
        </select>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-[#0F0F14] border border-white/10 rounded px-3 h-9 text-xs text-zinc-200"
          data-testid="rbac-directory-role-filter"
        >
          <option value="">All roles</option>
          {roles.map(r => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
        <div className="text-xs text-zinc-500 ml-auto">{filtered.length} staff</div>
      </div>

      <div className="term-card mc-scroll-neon" style={{ maxHeight: "72vh", overflowY: "auto" }} data-testid="rbac-staff-list">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#0F0F14] border-b border-white/5">
            <tr className="text-left text-[11px] uppercase text-zinc-500 sc-mono">
              <th className="px-4 py-2.5">Username</th>
              <th className="px-4 py-2.5">Departments</th>
              <th className="px-4 py-2.5">Roles</th>
              <th className="px-4 py-2.5">Perms</th>
              <th className="px-4 py-2.5">2FA</th>
              <th className="px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.map(s => (
              <tr key={s.id} data-testid={`rbac-staff-row-${s.username}`} className="hover:bg-white/[0.02]">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    {s.avatar ? (
                      <img src={s.avatar} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-white/10 grid place-items-center text-[10px]">{s.username[0]?.toUpperCase()}</div>
                    )}
                    <div>
                      <div className="text-white">{s.username}</div>
                      <div className="text-[10px] text-zinc-600">{s.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-xs text-zinc-400">
                  {s.departments.map(d => {
                    const label = catalog?.departments?.find(x => x.key === d)?.label || d;
                    return <span key={d} className="inline-block px-1.5 py-0.5 mr-1 rounded bg-white/5 border border-white/5 text-[10px]">{label}</span>;
                  })}
                </td>
                <td className="px-4 py-2.5">
                  {s.role_keys.map(rk => {
                    const r = roles.find(x => x.key === rk);
                    const isDeveloper = rk === "developer";
                    return (
                      <span
                        key={rk}
                        className={`inline-block px-1.5 py-0.5 mr-1 mb-1 rounded text-[10px] sc-mono ${
                          isDeveloper
                            ? "bg-[#00F0FF]/10 border border-[#00F0FF]/40 text-[#00F0FF]"
                            : "bg-white/5 border border-white/10 text-zinc-300"
                        }`}
                      >
                        {r?.name || rk}
                      </span>
                    );
                  })}
                </td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs sc-mono">{s.permission_count}</td>
                <td className="px-4 py-2.5 text-xs">
                  {s.totp_enabled
                    ? <span className="text-green-400">On</span>
                    : <span className="text-orange-400">Off</span>}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  {s.banned
                    ? <span className="text-red-400">Suspended</span>
                    : <span className="text-zinc-400">Active</span>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-zinc-600">No staff match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {!isDev && (
        <div className="mt-3 text-[11px] text-zinc-600 flex items-center gap-1.5">
          <Lock className="w-3 h-3" /> Only the Developer can edit roles or assign staff.
        </div>
      )}
    </div>
  );
}

/* ============================================================
 * Roles & Permissions (visual editor)
 * ============================================================ */
function RolesTab({ roles, catalog, isDev, onReload }) {
  const [selectedKey, setSelectedKey] = useState(roles[0]?.key || null);
  const selected = useMemo(() => roles.find(r => r.key === selectedKey) || null, [roles, selectedKey]);
  const [dirtyPerms, setDirtyPerms] = useState(null); // null = clean, Set = editing
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { setDirtyPerms(null); }, [selectedKey]);

  const currentPerms = useMemo(() => {
    if (dirtyPerms) return dirtyPerms;
    return new Set(selected?.permissions || []);
  }, [dirtyPerms, selected]);

  const togglePerm = (key) => {
    if (!isDev) return;
    const next = new Set(currentPerms);
    if (next.has(key)) next.delete(key); else next.add(key);
    setDirtyPerms(next);
  };

  const save = async () => {
    if (!selected || !dirtyPerms) return;
    setSaving(true);
    try {
      await api.patch(`/admin/rbac/roles/${selected.key}`, {
        permissions: Array.from(dirtyPerms),
      });
      toast.success(`Updated ${selected.name}`);
      setDirtyPerms(null);
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!selected) return;
    if (!window.confirm(`Reset ${selected.name} to code defaults?`)) return;
    try {
      await api.post(`/admin/rbac/roles/${selected.key}/reset-defaults`);
      toast.success("Reset to defaults");
      setDirtyPerms(null);
      await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  const cloneRole = async () => {
    if (!selected) return;
    const name = window.prompt("Name for the new role", `${selected.name} Copy`);
    if (!name) return;
    try {
      const r = await api.post("/admin/rbac/roles", {
        name, department: selected.department, description: `Cloned from ${selected.name}`,
        clone_from: selected.key, hierarchy_level: selected.hierarchy_level || 10,
      });
      toast.success(`Created ${r.data.name}`);
      await onReload();
      setSelectedKey(r.data.key);
    } catch (e) { toast.error(e?.response?.data?.detail || "Clone failed"); }
  };

  const deleteRole = async () => {
    if (!selected || selected.is_system) return;
    if (!window.confirm(`Delete role "${selected.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/rbac/roles/${selected.key}`);
      toast.success("Role deleted");
      await onReload();
      setSelectedKey(roles[0]?.key || null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-4">
      {/* Left: role list */}
      <div className="term-card mc-scroll-neon" style={{ maxHeight: "72vh", overflowY: "auto" }} data-testid="rbac-role-list">
        <div className="p-3 border-b border-white/5 flex items-center justify-between">
          <div className="text-xs sc-heading text-zinc-400">{roles.length} ROLES</div>
          {isDev && (
            <button
              onClick={() => setShowCreate(true)}
              data-testid="rbac-role-create-btn"
              className="text-[11px] sc-btn h-7 px-2 flex items-center gap-1"
            ><Plus className="w-3 h-3" /> New</button>
          )}
        </div>
        <div className="divide-y divide-white/5">
          {roles.map(r => (
            <button
              key={r.key}
              onClick={() => setSelectedKey(r.key)}
              data-testid={`rbac-role-row-${r.key}`}
              className={`w-full text-left px-3 py-2.5 hover:bg-white/[0.03] transition-colors ${selectedKey === r.key ? "bg-white/[0.05]" : ""}`}
            >
              <div className="flex items-center gap-2">
                <div className="text-sm text-white flex-1">{r.name}</div>
                {r.is_system && <Lock className="w-3 h-3 text-zinc-600" title="System role" />}
                {r.key === "developer" && <ShieldCheck className="w-3 h-3 text-[#00F0FF]" />}
              </div>
              <div className="text-[10px] text-zinc-600 sc-mono flex items-center gap-2 mt-0.5">
                <Building2 className="w-2.5 h-2.5" />
                {catalog?.departments?.find(d => d.key === r.department)?.label || r.department}
                <span className="ml-auto">{r.permissions.length}p</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right: permission editor */}
      <div className="term-card" data-testid="rbac-role-editor">
        {!selected && <div className="p-10 text-center text-zinc-500 text-sm">Select a role to view its permissions.</div>}
        {selected && (
          <>
            <div className="p-4 border-b border-white/5 flex items-center gap-3 flex-wrap">
              <div>
                <div className="sc-heading text-sm text-white">{selected.name}</div>
                <div className="text-[11px] text-zinc-500">{selected.description || "—"}</div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {isDev && !selected.is_system && (
                  <Button
                    variant="destructive" size="sm" onClick={deleteRole}
                    data-testid="rbac-role-delete"
                    className="h-8"
                  ><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
                )}
                {isDev && (
                  <>
                    <Button variant="outline" size="sm" onClick={cloneRole}
                      data-testid="rbac-role-clone" className="h-8 border-white/10 text-zinc-300">
                      <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
                    </Button>
                    <Button variant="outline" size="sm" onClick={resetDefaults}
                      data-testid="rbac-role-reset" className="h-8 border-white/10 text-zinc-300"
                      disabled={!selected.is_system && !catalog?.permissions?.some(p => selected.permissions.includes(p.key))}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                    </Button>
                    <Button size="sm" onClick={save} disabled={!dirtyPerms || saving}
                      data-testid="rbac-role-save"
                      className="h-8 bg-[#00F0FF] text-black hover:bg-[#66F7FF] disabled:opacity-50">
                      {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                      Save
                    </Button>
                  </>
                )}
              </div>
            </div>

            {selected.key === "developer" && (
              <div className="mx-4 mt-3 p-2 rounded bg-[#00F0FF]/[0.06] border border-[#00F0FF]/25 text-[11px] text-[#00F0FF]/90">
                <Lock className="w-3 h-3 inline mr-1" />
                The Developer role has every permission and cannot be edited from the UI. Its permission set is governed by the codebase.
              </div>
            )}

            <div className="p-4 mc-scroll-neon" style={{ maxHeight: "64vh", overflowY: "auto" }}>
              <PermissionEditor
                catalog={catalog}
                selected={currentPerms}
                onToggle={togglePerm}
                readOnly={!isDev || selected.key === "developer"}
              />
            </div>
          </>
        )}
      </div>

      {showCreate && (
        <CreateRoleModal
          catalog={catalog}
          onClose={() => setShowCreate(false)}
          onCreated={async (key) => { setShowCreate(false); await onReload(); setSelectedKey(key); }}
        />
      )}
    </div>
  );
}

/* Permission editor with collapsible categories + toggle switches */
function PermissionEditor({ catalog, selected, onToggle, readOnly }) {
  const [openCats, setOpenCats] = useState(() => new Set((catalog?.categories || []).map(c => c.key)));
  if (!catalog) return null;
  const toggleCat = (key) => {
    const next = new Set(openCats);
    if (next.has(key)) next.delete(key); else next.add(key);
    setOpenCats(next);
  };
  return (
    <div className="space-y-3">
      {catalog.categories.map(cat => {
        const catPerms = catalog.permissions.filter(p => p.category === cat.key);
        const activeCount = catPerms.filter(p => selected.has(p.key)).length;
        const open = openCats.has(cat.key);
        return (
          <div key={cat.key} className={`border rounded ${cat.sensitive ? "border-red-500/25 bg-red-500/[0.02]" : "border-white/10"}`} data-testid={`rbac-perm-cat-${cat.key}`}>
            <button
              onClick={() => toggleCat(cat.key)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left"
            >
              {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
              <div className={`text-xs sc-heading ${cat.sensitive ? "text-red-400" : "text-zinc-300"}`}>{cat.label}</div>
              <div className="ml-auto text-[10px] sc-mono text-zinc-500">{activeCount}/{catPerms.length}</div>
            </button>
            {open && (
              <div className="border-t border-white/5 divide-y divide-white/5">
                {catPerms.map(p => {
                  const on = selected.has(p.key);
                  const devOnly = p.developer_only;
                  return (
                    <label
                      key={p.key}
                      data-testid={`rbac-perm-${p.key}`}
                      className={`flex items-start gap-3 px-3 py-2 hover:bg-white/[0.02] ${readOnly ? "cursor-default" : "cursor-pointer"}`}
                    >
                      <button
                        type="button"
                        role="switch"
                        aria-checked={on}
                        onClick={() => !readOnly && !devOnly && onToggle(p.key)}
                        disabled={readOnly || devOnly}
                        className={`mt-0.5 relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
                          on ? "bg-[#00F0FF]" : "bg-white/10"
                        } ${(readOnly || devOnly) ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-black transition-transform ${on ? "translate-x-3.5" : "translate-x-0.5"}`} />
                      </button>
                      <div className="flex-1">
                        <div className="text-xs text-white flex items-center gap-2">
                          {p.label}
                          {devOnly && <span className="px-1 py-0.5 rounded bg-[#00F0FF]/10 border border-[#00F0FF]/25 text-[9px] text-[#00F0FF]">DEV</span>}
                        </div>
                        <div className="text-[10px] text-zinc-500 sc-mono">{p.key}</div>
                        {p.description && <div className="text-[10px] text-zinc-500 mt-0.5">{p.description}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CreateRoleModal({ catalog, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [department, setDepartment] = useState(catalog?.departments?.[0]?.key || "development");
  const [description, setDescription] = useState("");
  const [hier, setHier] = useState(10);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/rbac/roles", {
        name: name.trim(), department, description: description.trim(),
        hierarchy_level: hier, permissions: [],
      });
      toast.success("Role created");
      onCreated(r.data.key);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4">
      <form onSubmit={submit} className="w-full max-w-md term-card p-5" data-testid="rbac-create-role-modal">
        <div className="flex items-center gap-2 mb-4">
          <div className="sc-heading text-sm text-white flex-1">New Role</div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-zinc-500 sc-mono">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-[#0F0F14] border-white/10" data-testid="rbac-create-role-name" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-500 sc-mono">Department</label>
            <select value={department} onChange={(e) => setDepartment(e.target.value)} className="mt-1 w-full bg-[#0F0F14] border border-white/10 rounded px-3 h-9 text-sm text-zinc-200" data-testid="rbac-create-role-dept">
              {(catalog?.departments || []).map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-zinc-500 sc-mono">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 bg-[#0F0F14] border-white/10" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-500 sc-mono">Hierarchy level (higher = more senior)</label>
            <Input type="number" value={hier} min={0} max={100} onChange={(e) => setHier(parseInt(e.target.value, 10) || 0)} className="mt-1 bg-[#0F0F14] border-white/10" />
          </div>
        </div>
        <div className="flex gap-2 mt-4 justify-end">
          <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-zinc-300">Cancel</Button>
          <Button type="submit" disabled={busy || name.trim().length < 2} className="bg-[#00F0FF] text-black hover:bg-[#66F7FF]" data-testid="rbac-create-role-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ============================================================
 * Assignments (assign / revoke a role)
 *
 * Delegated permissions:
 * - `staff.hire`   → can assign roles below your own hierarchy_level.
 * - `staff.revoke` → SECONDARY permission (Developer-granted) to revoke.
 * - Developer bypasses both hierarchy + permission checks.
 * ============================================================ */
function AssignmentsTab({ assignments, roles, me, onReload }) {
  const [username, setUsername] = useState("");
  const [roleKey, setRoleKey] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const isDev = !!me?.is_developer;
  const canHire = !!me?.can_hire;
  const canRevoke = !!me?.can_revoke;
  const requiresApproval = !!me?.requires_approval_to_hire;
  const myMaxLevel = me?.max_hierarchy_level ?? 0;

  // Roles this user is allowed to assign — strictly below their own
  // hierarchy_level. Developer sees everything. `developer` role is
  // Developer-only regardless of hierarchy.
  const assignableRoles = useMemo(() => {
    if (isDev) return roles;
    return roles.filter(r =>
      r.key !== "developer" && Number(r.hierarchy_level ?? 0) < myMaxLevel
    );
  }, [roles, isDev, myMaxLevel]);

  const canRevokeAssignment = (a) => {
    if (isDev) return true;
    if (!canRevoke) return false;
    if (a.role_key === "developer") return false;
    const role = roles.find(r => r.key === a.role_key);
    if (!role) return false;
    return Number(role.hierarchy_level ?? 0) < myMaxLevel;
  };

  const assign = async (e) => {
    e.preventDefault();
    if (!username.trim() || !roleKey) return;
    setBusy(true);
    try {
      const resp = await api.post("/admin/rbac/assignments", { username: username.trim(), role_key: roleKey, notes });
      // Backend returns 202 with { pending: {...}, message } when the request
      // goes through the approval queue instead of applying directly.
      if (resp.status === 202 || resp.data?.pending) {
        toast.success(resp.data?.message || "Proposal queued for CEO / Developer approval.");
      } else {
        toast.success(`Assigned ${roleKey} to ${username}`);
      }
      setUsername(""); setNotes(""); setRoleKey("");
      await onReload();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };

  const revoke = async (a) => {
    if (!window.confirm(`Revoke role "${a.role_key}" from ${a.username}?`)) return;
    try {
      await api.delete(`/admin/rbac/assignments/${a.id}`);
      toast.success("Revoked");
      await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-4">
      {/* Assign form */}
      <div className="term-card p-4" data-testid="rbac-assign-panel">
        <div className="sc-heading text-sm text-white mb-1 flex items-center gap-2"><UserPlus className="w-4 h-4 text-[#00F0FF]" /> Assign Role</div>
        {/* Delegation status banner */}
        <div className="text-[10px] sc-mono text-zinc-500 mb-3 space-y-0.5">
          <div>
            Hire: {canHire
              ? <span className="text-green-400">Enabled</span>
              : <span className="text-zinc-600">Disabled</span>}
            <span className="text-zinc-600"> · max level below <span className="text-white">{isDev ? "∞" : myMaxLevel}</span></span>
          </div>
          <div>
            Revoke: {canRevoke
              ? <span className="text-green-400">Enabled</span>
              : <span className="text-orange-400">Requires Developer grant</span>}
          </div>
        </div>

        {!canHire && (
          <div className="text-[11px] text-zinc-500 flex items-start gap-1.5 mb-3 p-2 rounded bg-white/[0.02] border border-white/5">
            <Lock className="w-3 h-3 mt-0.5 shrink-0" />
            <span>You don&apos;t have the <span className="text-white sc-mono">staff.hire</span> permission. The Developer can grant it from the Roles tab.</span>
          </div>
        )}

        {canHire && requiresApproval && (
          <div
            className="text-[11px] text-zinc-300 flex items-start gap-1.5 mb-3 p-2 rounded bg-[color:var(--sc-orange)]/10 border border-[color:var(--sc-orange)]/30"
            data-testid="rbac-approval-notice"
          >
            <Bell className="w-3 h-3 mt-0.5 shrink-0 text-[color:var(--sc-orange)]" />
            <span>
              Your proposal will be queued for <span className="text-white font-bold">Developer / CEO approval</span> before it goes live. You&apos;ll see it in the <span className="text-white">Pending Approvals</span> tab until it&apos;s reviewed.
            </span>
          </div>
        )}

        <form onSubmit={assign} className="space-y-3">
          <div>
            <label className="text-[11px] text-zinc-500 sc-mono">Username</label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. ArctiCaster" className="mt-1 bg-[#0F0F14] border-white/10" data-testid="rbac-assign-username" disabled={!canHire} />
          </div>
          <div>
            <label className="text-[11px] text-zinc-500 sc-mono">
              Role
              {!isDev && <span className="text-zinc-600 ml-2">· {assignableRoles.length} available (below level {myMaxLevel})</span>}
            </label>
            <select value={roleKey} onChange={(e) => setRoleKey(e.target.value)} disabled={!canHire} className="mt-1 w-full bg-[#0F0F14] border border-white/10 rounded px-3 h-9 text-sm text-zinc-200" data-testid="rbac-assign-role-select">
              <option value="">Select a role…</option>
              {assignableRoles.map(r => <option key={r.key} value={r.key}>{r.name} (lvl {r.hierarchy_level ?? 0}, {r.permissions.length}p)</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-zinc-500 sc-mono">Notes (optional)</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why this assignment?" className="mt-1 bg-[#0F0F14] border-white/10" disabled={!canHire} />
          </div>
          <Button type="submit" disabled={!canHire || busy || !username || !roleKey} className="w-full bg-[#00F0FF] text-black hover:bg-[#66F7FF] disabled:opacity-40" data-testid="rbac-assign-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (requiresApproval ? "Propose for approval" : "Assign")}
          </Button>
        </form>
      </div>

      {/* Current assignments */}
      <div className="term-card mc-scroll-neon" style={{ maxHeight: "72vh", overflowY: "auto" }} data-testid="rbac-assignments-list">
        <div className="p-3 border-b border-white/5 flex items-center">
          <div className="text-xs sc-heading text-zinc-400">Current Assignments</div>
          <div className="ml-auto text-xs text-zinc-500">{assignments.length}</div>
        </div>
        <div className="divide-y divide-white/5">
          {assignments.map(a => {
            const role = roles.find(r => r.key === a.role_key);
            const revokable = canRevokeAssignment(a);
            return (
              <div key={a.id} className="p-3 flex items-center gap-3" data-testid={`rbac-assignment-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">
                    {a.username} <span className="text-zinc-500">·</span> <span className="text-[#00F0FF]">{role?.name || a.role_key}</span>
                    {role && <span className="ml-2 text-[10px] text-zinc-600 sc-mono">lvl {role.hierarchy_level ?? 0}</span>}
                  </div>
                  <div className="text-[10px] text-zinc-500 sc-mono">
                    by {a.assigned_by} · {new Date(a.assigned_at).toLocaleString()}
                    {a.notes && <span> · <span className="text-zinc-400">{a.notes}</span></span>}
                  </div>
                </div>
                {revokable ? (
                  <button
                    onClick={() => revoke(a)}
                    className="text-red-400 hover:text-red-300 p-1.5"
                    title="Revoke"
                    data-testid={`rbac-assignment-revoke-${a.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <div
                    className="text-zinc-700 p-1.5"
                    title={
                      !canRevoke
                        ? "Requires the staff.revoke permission from the Developer."
                        : a.role_key === "developer"
                          ? "Only the Developer can revoke the Developer role."
                          : `Role hierarchy is at or above yours (${myMaxLevel}).`
                    }
                  >
                    <Lock className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
            );
          })}
          {assignments.length === 0 && <div className="p-6 text-center text-xs text-zinc-500">No assignments yet.</div>}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * Audit Log
 * ============================================================ */
function AuditTab({ audit, onReload }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="text-xs sc-heading text-zinc-400">{audit.length} entries</div>
        <button onClick={onReload} className="ml-auto text-xs sc-btn h-7 px-2" data-testid="rbac-audit-reload">Reload</button>
      </div>
      <div className="term-card mc-scroll-neon" style={{ maxHeight: "72vh", overflowY: "auto" }} data-testid="rbac-audit-list">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#0F0F14] border-b border-white/5">
            <tr className="text-left text-[11px] uppercase text-zinc-500 sc-mono">
              <th className="px-4 py-2.5">Time (UTC)</th>
              <th className="px-4 py-2.5">Actor</th>
              <th className="px-4 py-2.5">Action</th>
              <th className="px-4 py-2.5">Target</th>
              <th className="px-4 py-2.5">Before → After</th>
              <th className="px-4 py-2.5">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {audit.map(e => (
              <tr key={e.id} className="hover:bg-white/[0.02]" data-testid={`rbac-audit-row-${e.id}`}>
                <td className="px-4 py-2 text-[11px] text-zinc-400 sc-mono whitespace-nowrap">{e.timestamp?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-2 text-xs text-white">{e.actor_username}</td>
                <td className="px-4 py-2 text-xs"><span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[10px] sc-mono text-[#00F0FF]">{e.action}</span></td>
                <td className="px-4 py-2 text-xs text-zinc-400 sc-mono">{e.target}</td>
                <td className="px-4 py-2 text-[10px] text-zinc-500 max-w-md truncate" title={JSON.stringify(e.before) + " → " + JSON.stringify(e.after)}>
                  {shortDiff(e.before, e.after)}
                </td>
                <td className="px-4 py-2 text-[10px] text-zinc-500 sc-mono">{e.actor_ip || "—"}</td>
              </tr>
            ))}
            {audit.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-zinc-600">No audit entries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function shortDiff(before, after) {
  if (!before && !after) return "—";
  if (Array.isArray(before?.permissions) && Array.isArray(after?.permissions)) {
    const b = new Set(before.permissions), a = new Set(after.permissions);
    const added = [...a].filter(x => !b.has(x));
    const removed = [...b].filter(x => !a.has(x));
    const parts = [];
    if (added.length) parts.push(`+${added.length}`);
    if (removed.length) parts.push(`-${removed.length}`);
    return parts.join(" ") || "no perm changes";
  }
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const parts = [];
  keys.forEach(k => {
    const b = before?.[k]; const a = after?.[k];
    if (JSON.stringify(b) !== JSON.stringify(a)) parts.push(`${k}: ${JSON.stringify(b)} → ${JSON.stringify(a)}`);
  });
  return parts.join(", ") || "—";
}

/* ============================================================
 * Pending Approvals
 *
 * Any executive with `staff.hire` (but not Developer/CEO) creates a pending
 * proposal instead of a direct assignment. This tab lets Developer/CEO
 * approve or reject, and lets proposers see (and cancel) their own.
 * ============================================================ */
function PendingTab({ pending, me, onReload }) {
  const [busyId, setBusyId] = useState(null);
  const canApprove = !!me?.can_approve;
  const myUsername = me?.username;

  const decide = async (p, action) => {
    const notes = action === "reject"
      ? window.prompt("Reason for rejection (optional):", "")
      : (window.prompt("Approval note (optional):", "") ?? "");
    if (notes === null) return;  // user hit cancel on prompt
    setBusyId(p.id);
    try {
      await api.post(`/admin/rbac/pending/${p.id}/${action}`, { decision_notes: notes || "" });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || `${action} failed`); }
    finally { setBusyId(null); }
  };

  const cancel = async (p) => {
    if (!window.confirm("Cancel this pending proposal?")) return;
    setBusyId(p.id);
    try {
      await api.delete(`/admin/rbac/pending/${p.id}`);
      toast.success("Cancelled");
      await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
    finally { setBusyId(null); }
  };

  const mine = pending.filter(p => p.proposer_username === myUsername);
  const others = pending.filter(p => p.proposer_username !== myUsername);

  const renderRow = (p) => {
    const isMine = p.proposer_username === myUsername;
    const busy = busyId === p.id;
    return (
      <div key={p.id} className="p-3 flex items-center gap-3" data-testid={`rbac-pending-${p.id}`}>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">
            <span className="text-zinc-400">{p.proposer_username}</span>
            <span className="text-zinc-600 mx-1.5">wants to hire</span>
            <span className="text-white font-bold">{p.target_username}</span>
            <span className="text-zinc-500 mx-1.5">as</span>
            <span className="text-[#00F0FF]">{p.role_name}</span>
            <span className="ml-2 text-[10px] text-zinc-600 sc-mono">lvl {p.role_hierarchy_level}</span>
          </div>
          <div className="text-[10px] text-zinc-500 sc-mono">
            {new Date(p.created_at).toLocaleString()}
            {p.notes && <span> · <span className="text-zinc-400">{p.notes}</span></span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canApprove && !isMine && (
            <>
              <button
                onClick={() => decide(p, "approve")}
                disabled={busy}
                className="px-2 py-1 rounded bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 text-xs flex items-center gap-1 disabled:opacity-40"
                data-testid={`rbac-pending-approve-${p.id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Approve
              </button>
              <button
                onClick={() => decide(p, "reject")}
                disabled={busy}
                className="px-2 py-1 rounded bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-xs flex items-center gap-1 disabled:opacity-40"
                data-testid={`rbac-pending-reject-${p.id}`}
              >
                <XCircle className="w-3 h-3" /> Reject
              </button>
            </>
          )}
          {isMine && (
            <button
              onClick={() => cancel(p)}
              disabled={busy}
              className="px-2 py-1 rounded border border-white/10 text-zinc-400 hover:bg-white/5 text-xs disabled:opacity-40"
              data-testid={`rbac-pending-cancel-${p.id}`}
            >
              Cancel
            </button>
          )}
          {!canApprove && !isMine && (
            <div className="text-[10px] text-zinc-600 sc-mono" title="Awaiting Developer or CEO approval">awaiting review</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {canApprove && (
        <div className="term-card p-3 flex items-center gap-2" data-testid="rbac-pending-approver-banner">
          <Bell className="w-4 h-4 text-[color:var(--sc-orange)] shrink-0" />
          <div className="text-xs text-zinc-300">
            You&apos;re a <span className="text-white font-bold">{me?.is_developer ? "Developer" : "CEO"}</span> — approve or reject proposals from your executive team below.
          </div>
          <div className="ml-auto text-xs text-zinc-500">{pending.length} awaiting</div>
        </div>
      )}

      {mine.length > 0 && (
        <div className="term-card" data-testid="rbac-pending-mine">
          <div className="p-3 border-b border-white/5 text-xs sc-heading text-zinc-400">Your proposals</div>
          <div className="divide-y divide-white/5">{mine.map(renderRow)}</div>
        </div>
      )}

      <div className="term-card" data-testid="rbac-pending-all">
        <div className="p-3 border-b border-white/5 text-xs sc-heading text-zinc-400 flex items-center">
          {canApprove ? "Requires your review" : "All pending proposals"}
          <span className="ml-auto text-[11px] text-zinc-500">{others.length}</span>
        </div>
        {others.length === 0 ? (
          <div className="p-8 text-center text-xs text-zinc-500">
            {canApprove ? "Inbox zero — no proposals awaiting review." : "No proposals from other executives right now."}
          </div>
        ) : (
          <div className="divide-y divide-white/5">{others.map(renderRow)}</div>
        )}
      </div>
    </div>
  );
}

